import { useContext } from "react";

import classnames from "classnames";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";

import { GalleryModalContext } from "components/pages/property/listing/components/GalleryModal/context";
import { TripadvisorAwardBadge } from "components/pages/search/components/TripadvisorAwardBadge";
import { PropertyListingContext } from "components/store/source";
import { buildingListingTrackingEvents } from "components/store/source/tracking_events";
import { Image, SwiperCarousel } from "components/ui/source";


import ViewAllPhotosButton from "./ViewAllPhotosButton";

import styles from "./PhotosGrid.module.scss";

function PhotosCarousel({ photos, onPhotoClick }) {
  const carouselProps = {
    lazyLoad: true,
    loop: true,
    loopedSlides: photos.length,
    slidesPerView: 1,
  };

  return (
    <SwiperCarousel
      className={styles.photoCarousel}
      carouselProps={carouselProps}
    >
      {photos.map((photo, index) => (
        <button
          key={`property-listing-photos-carousel-${index}`}
          className={styles.photoSlide}
          onClick={onPhotoClick(index)}
        >
          <Image
            className="swiper-lazy"
            src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
            data-src={photo.mediumSrc}
            lazyLoad={false}
            alt={photo.caption || ""}
          />
        </button>
      ))}
    </SwiperCarousel>
  );
}

PhotosCarousel.propTypes = {
  photos: PropTypes.array,
  onPhotoClick: PropTypes.func,
};

function PhotosGrid({ className, showBuildingPhotos, tripadvisorAward }) {
  const { listing } = useContext(PropertyListingContext);

  let photos = listing?.photos || [];
  // Use building photos if collectionId is provided
  if (showBuildingPhotos && listing?.buildingPhotos?.length > 0) {
    photos = listing?.buildingPhotos;
  }

  const { handleOpen } = useContext(GalleryModalContext);

  const onPhotoClick = photoPosition => e => {
    e.stopPropagation();

    return handleOpen(
      photos,
      photoPosition,
      buildingListingTrackingEvents.galleryViewed,
      { listingId: listing?.id, buildingListing: true },
    );
  };

  const photoElements = photos
    .slice(0, Math.min(3, photos.length))
    .map((photo, index) => (
      <button
        className={styles.photoContainer}
        key={`property-listing-photos-${index}`}
        onClick={onPhotoClick(index)}
      >
        <Image
          src={index === 0 ? photo.largeSrc : photo.mediumSrc}
          alt={photo.caption || ""}
        />
      </button>
    ));

  // Fallback if less than 3 images are available
  if (photoElements.length === 0) return null;
  const layout = `photosGrid${photoElements.length}`;

  return (
    <div
      className={classnames(styles[layout], styles.container, className)}
      data-testid="PhotosGrid"
    >
      {photoElements}
      {!isEmpty(tripadvisorAward) && (
        <TripadvisorAwardBadge
          className={styles.tripadvisorAwardBadge}
          tripadvisorAward={tripadvisorAward}
        />
      )}
      <PhotosCarousel photos={photos} onPhotoClick={onPhotoClick} />
      <ViewAllPhotosButton
        className={styles.viewAllPhotos}
        onClick={onPhotoClick(0)}
      />
    </div>
  );
}

PhotosGrid.propTypes = {
  className: PropTypes.string,
  showBuildingPhotos: PropTypes.bool,
};

export default PhotosGrid;
